import React, { useEffect, useRef } from "react";
import { Box, Typography, Stack, keyframes } from "@mui/material";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

const scrollLeft = keyframes`
  from { transform: translateX(0); }
  to { transform: translateX(-100%); }
`;

const scrollRight = keyframes`
  from { transform: translateX(-100%); }
  to { transform: translateX(0); }
`;

const brandsRow1 = [
  "نی‌موبایل",
  "کاوش اندیشه",
  "کُنته",
  "رستوران فردیس",
  "توزیع برق شرکت برق",
  "آجیل نیک‌نام",
  "رابی ایران",
  "میل‌لند",
  "ویتا نوش",
  " املاک صداقیان",
  "طراحی فرش نقش و نگار",
];

const brandsRow2 = [
  "سازمان استاندارد",
  "ویوان",
  "لوکا",
  "ماهرخ سار",
  "گروه مهندسی لاین",
  "یاس",
  "رستوران آرامش",
  "منو آنلاین",
  "اتحادیه طرقبه شاندیز",
  "صرافی سیگلوی",
];
export default function TrustedPartners() {
  const sectionRef = useRef(null);
  const titleRef = useRef(null);
  const track1Ref = useRef(null);
  const track2Ref = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.fromTo(
        titleRef.current,
        { opacity: 0, y: 40 },
        {
          opacity: 1,
          y: 0,
          duration: 1,
          ease: "expo.out",
          scrollTrigger: {
            trigger: sectionRef.current,
            start: "top 85%",
          },
        },
      );

      gsap.fromTo(
        track1Ref.current,
        { opacity: 0, x: 50 },
        {
          opacity: 1,
          x: 0,
          duration: 1.2,
          ease: "power3.out",
          scrollTrigger: {
            trigger: sectionRef.current,
            start: "top 75%",
          },
        },
      );

      gsap.fromTo(
        track2Ref.current,
        { opacity: 0, x: -50 },
        {
          opacity: 1,
          x: 0,
          duration: 1.2,
          delay: 0.2,
          ease: "power3.out",
          scrollTrigger: {
            trigger: sectionRef.current,
            start: "top 75%",
          },
        },
      );
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  const LogoBox = ({ name }) => (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        minWidth: { xs: 160, md: 220 },
        height: { xs: 80, md: 100 },
        mx: { xs: 1, md: 2 },
        borderRadius: "16px",
        bgcolor: "rgba(255, 255, 255, 0.02)",
        border: "1px solid rgba(255, 255, 255, 0.05)",
        cursor: "pointer",
        transition: "all 0.4s ease",
        "&:hover": {
          bgcolor: "rgba(255, 255, 255, 0.05)",
          borderColor: "#00ffcc",
          transform: "translateY(-5px)",
          boxShadow: "0 10px 30px rgba(0, 255, 204, 0.1)",
          "& .brand-name": {
            color: "#fff",
            opacity: 1,
          },
        },
      }}
    >
      <Typography
        className="brand-name"
        sx={{
          fontSize: { xs: 16, md: 20 },
          fontWeight: 700,
          color: "#fff",
          opacity: 0.3,
          transition: "all 0.4s ease",
          letterSpacing: "1px",
        }}
      >
        {name}
      </Typography>
    </Box>
  );

  return (
    <Box
      ref={sectionRef}
      sx={{
        py: { xs: 8, md: 12 },
        bgcolor: "#030303",
        color: "#fff",
        overflow: "hidden",
      }}
    >
      <Stack
        ref={titleRef}
        alignItems="center"
        textAlign="center"
        mb={8}
        px={2}
      >
        <Typography
          sx={{
            color: "#00ffcc",
            fontSize: { xs: 12, md: 14 },
            fontWeight: 600,
            letterSpacing: "3px",
            textTransform: "uppercase",
            mb: 1.5,
          }}
        >
          Trusted by Visionaries
        </Typography>
        <Typography
          sx={{
            fontSize: { xs: 30, lg: 44 },
            fontWeight: 700,
            letterSpacing: "-1px",
          }}
        >
          هم‌مسیر با پیشگامـان
        </Typography>
      </Stack>

      <Box sx={{ position: "relative" }}>
        {/* Gradients */}
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            width: { xs: "60px", md: "200px" },
            height: "100%",
            background:
              "linear-gradient(to right, #030303 0%, transparent 100%)",
            zIndex: 2,
            pointerEvents: "none",
          }}
        />
        <Box
          sx={{
            position: "absolute",
            top: 0,
            right: 0,
            width: { xs: "60px", md: "200px" },
            height: "100%",
            background:
              "linear-gradient(to left, #030303 0%, transparent 100%)",
            zIndex: 2,
            pointerEvents: "none",
          }}
        />

        {/* First Row */}
        <Box
          ref={track1Ref}
          sx={{
            display: "flex",
            mb: { xs: 2, md: 4 },
            "&:hover .marquee-content": { animationPlayState: "paused" },
          }}
        >
          {/* double map items */}
          <Box
            className="marquee-content"
            sx={{
              display: "flex",
              width: "max-content",
              flexShrink: 0,
              animation: `${scrollLeft} 30s linear infinite`,
            }}
          >
            {brandsRow1.map((brand, index) => (
              <LogoBox key={`r1-a-${index}`} name={brand} />
            ))}
          </Box>
          <Box
            className="marquee-content"
            sx={{
              display: "flex",
              width: "max-content",
              flexShrink: 0,
              animation: `${scrollLeft} 30s linear infinite`,
            }}
          >
            {brandsRow1.map((brand, index) => (
              <LogoBox key={`r1-b-${index}`} name={brand} />
            ))}
          </Box>
        </Box>

        {/* Second Row*/}
        <Box
          ref={track2Ref}
          sx={{
            display: "flex",
            "&:hover .marquee-content": { animationPlayState: "paused" },
          }}
        >
          <Box
            className="marquee-content"
            sx={{
              display: "flex",
              width: "max-content",
              flexShrink: 0,
              animation: `${scrollRight} 30s linear infinite`,
            }}
          >
            {brandsRow2.map((brand, index) => (
              <LogoBox key={`r2-a-${index}`} name={brand} />
            ))}
          </Box>
          <Box
            className="marquee-content"
            sx={{
              display: "flex",
              width: "max-content",
              flexShrink: 0,
              animation: `${scrollRight} 30s linear infinite`,
            }}
          >
            {brandsRow2.map((brand, index) => (
              <LogoBox key={`r2-b-${index}`} name={brand} />
            ))}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
